import { useEffect, useMemo, useState } from 'react';
import { Button, SearchInput, Icon, TwModal as Modal, Text, TwButton, Input, TwFileUpload as FileUpload } from '@ui-component-library';
import { getMunis } from '../../../api/services/main';
import { FileItem } from '@ui-component-library/components/FileUpload/FileUpload.types';
import { virkespriserPost } from '../../../api/services/prices'

type VirkespriskollemForm = {
    muni: string
    volume: string
    anbud: FileItem[]
    anbudManuellt: string
    anbudsDatum: string
    email: string
    phonenumber: string
}

type VirkespriskollenModalProps = {
    setOpenVirkespriskollen: (open: boolean) => void;
    openVirkespriskollen: boolean;
};

export const VirkespriskollenModal = ({ setOpenVirkespriskollen, openVirkespriskollen }: VirkespriskollenModalProps) => {

    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 5
    const FORM_STEPS_COUNT = 3;
    const displayStepIndex = currentStep - 1;
    const displayPercentage = Math.round((displayStepIndex / FORM_STEPS_COUNT) * 100);
    const [munis, setMunis] = useState<{ label: string; value: string }[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [submitError, setSubmitError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<VirkespriskollemForm>({
        muni: '',
        volume: '',
        anbud: [],
        anbudManuellt: '',
        anbudsDatum: '',
        email: (typeof Window !== 'undefined' && (Window as any).user_data?.email) ?? '',
        phonenumber: (typeof Window !== 'undefined' && (Window as any).user_data?.mobilePhoneNumber) ?? '',
    });

    const isCurrentStepValid = () => {
        switch (currentStep) {
            case 2:
                return Boolean(formData.muni?.trim() && formData.volume?.trim());
            case 3:
                return Boolean(formData.anbud?.length || formData.anbudManuellt?.trim());
            case 4:
                return Boolean(formData.email?.trim() && formData.phonenumber?.trim());
            case 5:
                return true;
            default:
                return true;
        }
    };
    const filteredMunis = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return munis;
        return munis.filter((m) => m.label.toLowerCase().includes(q));
    }, [munis, searchQuery]);

    const selectedMuniLabel = useMemo(
        () => (formData.muni ? munis.find((m) => m.value === formData.muni)?.label ?? '' : ''),
        [formData.muni, munis]
    );

    useEffect(() => {
        let cancelled = false;
        getMunis().then((resp) => {
            if (cancelled || !resp?.ok) return;
            const list = resp.data?.munis ?? [];

            setMunis(
                list.map((m: { label?: string; value?: string }) => ({
                    label: m.label ?? '',
                    value: String(m.value ?? m.label ?? ''),
                }))
            );
        });
        return () => { cancelled = true; };
    }, []);

    const getButtonText = () => {
        if (currentStep === 1) return 'Starta';
        if (currentStep === totalSteps) return 'Stäng';
        if (currentStep === 4) return isSubmitting ? 'Skickar...' : submitError ? 'Försök igen' : 'Skicka';
        return 'Nästa';
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        setSubmitError(false);
        setIsSubmitting(true);
        try {
            const response = await virkespriserPost(formData);
            if (response.ok) {
                setCurrentStep(currentStep + 1);
            } else {
                console.error('Failed to post virkespriser');
                setSubmitError(true);
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <div className="z-[var(--z-modal)]">
            <Modal isOpen={openVirkespriskollen}>
            <Modal.Header>
                <div className="relative flex w-full items-center justify-between min-h-[38px]">
                    <div className="w-[38px] min-w-[38px] shrink-0 flex items-center justify-center">
                        {currentStep > 1 && currentStep < totalSteps ? (
                            <TwButton
                                variant="tertiary"
                                size="icon"
                                onClick={handlePrevious}
                            >
                                <Icon name="ArrowRightSimple" size="large" className="rotate-180" />
                            </TwButton>
                        ) : null}
                    </div>
                    <div className="text-center px-4">
                        <Text variant="text-base/leading-normal/medium">
                            Virkespriskollen
                        </Text>
                    </div>
                    <div className="w-[38px] min-w-[38px] shrink-0 flex items-center justify-center">
                        {currentStep !== totalSteps ? (
                            <TwButton
                                variant="tertiary"
                                size="icon"
                                onClick={() => setOpenVirkespriskollen(false)}
                            >
                                <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
                            </TwButton>
                        ) : null}
                    </div>
                </div>
            </Modal.Header>
            {currentStep > 1 && currentStep < totalSteps && (
                <div className="bg-sandstone-light px-4 py-3 md:px-6">
                    <div className="mb-2 flex items-center justify-between">
                        <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
                            Steg {displayStepIndex} av {FORM_STEPS_COUNT}
                        </Text>
                    </div>
                    <div className="bg-charcoal-alpha-50 h-2 w-full overflow-hidden rounded-lg">
                        <div
                            className="h-full rounded-lg bg-[#349269] transition-all duration-300 ease-in-out"
                            style={{ width: `${displayPercentage}%` }}
                        />
                    </div>
                </div>
            )}
            <Modal.Content className="bg-sandstone-light">
                {/* Step 1  */}
                {currentStep === 1 &&
                <>
                    <div className="flex justify-center">
                        <img
                            src={Window.static_path + "img/other/Man-with-chainsaw.webp"}
                            alt="man with chainsaw"
                            className="rounded-xl overflow-hidden w-full h-[280px] object-cover object-center"
                        />
                    </div> 
                    <div className="max-w-[500px] py-6">
                        <Text variant="text-lg/leading-normal/normal" className="mb-2">
                            Låt oss utvärdera dina anbud, helt kostnadsfritt
                        </Text>
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2">
                            Har du fått ett avverknings- eller kontraktsförslag? Det kan vara svårt att avgöra om villkoren faktiskt är de bästa för just din skog. Vi hjälper dig att utvärdera priser och jämföra olika anbud, så att du kan fatta beslut med trygghet.
                        </Text>
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-4">
                            Som en oberoende partner har vi din rygg. Vi identifierar eventuella risker i kontraktet och ser till att du får ut det fulla värdet av din skogsaffär.
                            Kostnadsfritt och utan förbindelser.
                        </Text>
                        <Text variant="text-sm/leading-normal/bold">
                            Fyll i detta formulär så hjälper vi dig mot en bra virkesaffär.

                        </Text>
                    </div>
                </>
                }
                {/* Step 2  */}
                {currentStep === 2 &&
                    <>
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2">
                            I vilken kommun har du skogen? *
                        </Text>
                        <SearchInput
                            className='mb-8'
                            name="muni"
                            label="Sök eller välj kommun"
                            placeholder="Välj kommun"
                            value={selectedMuniLabel || searchQuery}
                            results={filteredMunis}
                            onChange={(val) => {
                                setSearchQuery(val);
                                if (formData.muni && val !== selectedMuniLabel) {
                                    setFormData((prev) => ({ ...prev, muni: '' }));
                                }
                            }}
                            onSelect={(option) => {
                                setFormData((prev) => ({ ...prev, muni: String(option.value) }));
                                setSearchQuery('');
                            }}
                        />
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2">
                            Hur stor volymen ska du avverka eller gallra? *
                        </Text>
                        <Text variant="text-xs/leading-normal/normal" className="text-[#737373] mb-2">
                            Om du inte vet hur stor volymen är kan du också ange ungefärlig areal i hektar. Om du ska sälja biobränsle kan du ange ungefärlig längd, höjd och bredd på din GROT eller brännvedshög.
                        </Text>
                        <Input
                            name="volume"
                            type="text"
                            label="Ange informationen nedan på valfritt sätt"
                            variant="fluid"
                            value={formData.volume ?? ''}
                            onChange={(value) => setFormData((prev) => ({ ...prev, volume: value }))}
                        />
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2 mt-8">
                            Vilket datum fick du anbuden?
                        </Text>
                        <Input
                            name="date"
                            type="date"
                            label="Exempelvis: 2025-01-15"
                            variant="fluid"
                            //Min and Max is needed to make the input from the user move to next step correctly
                            min="2000-01-01"
                            max="3000-01-01"
                            value={formData.anbudsDatum}
                            onChange={(value) => setFormData((prev) => ({ ...prev, anbudsDatum: value }))}
                        />
                    </>
                }

                {/* Step 3 */}
                {currentStep === 3 &&
                    <>
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2 mt-6">
                            Bifoga de anbud du har fått in. Det går också bra att bifoga bild om du exempelvis fått prisförslag per mail
                            Har du fått en PDF med avverknings- eller kontraktsförslag bifogar du den här. Det går lika bra med andra filformat och/eller bild på anbud via mail.
                        </Text>
                        <FileUpload
                            name="anbud"
                            label="Anbud"
                            buttonText='Välj eller drag fil här'
                            value={formData.anbud ?? []}
                            multiple
                            onChange={(value) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    anbud: value.map((f) => ({ ...f, valid: true })),
                                }))
                            }
                            errorText='Storleksgräns: 10MB'
                            successText='Klart'
                            idleText='OBS! Om du inte har möjligt kan du ange ditt anbud manuellt nedan.'
                        />

                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2 mt-6">
                            Om du inte har möjlighet att bifoga ditt anbud så går det bra att skriva det i fältet nedan.
                        </Text>

                        <Input
                            name="anbudManuellt"
                            type="text"
                            label="Exempelvis: Grantimmer 700kr/m3fub"
                            variant="fluid"
                            value={formData.anbudManuellt ?? ''}
                            onChange={(value) => setFormData((prev) => ({ ...prev, anbudManuellt: value }))}
                        />
                    </>
                }

                {/* Step 4 */}
                {currentStep === 4 &&
                    <>
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2 mt-6">
                            Ange din e-post
                        </Text>
                        <Input
                            name="email"
                            type="email"
                            label="Ange den e-postadress vi kan nå dig på"
                            variant="fluid"
                            value={formData.email ?? (Window.user_data.email)}
                            onChange={(value) => setFormData((prev) => ({ ...prev, email: value }))}
                        />
                        <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mb-2 mt-6">
                            Ange dtt telefonnummer
                        </Text>
                        <Input
                            name="phonenumber"
                            type="phone"
                            label="Ange det telefonnummer som vi kan nå dig på"
                            variant="fluid"
                            value={formData.phonenumber ?? (Window.user_data.phonenumber)}
                            onChange={(value) => setFormData((prev) => ({ ...prev, phonenumber: value }))}
                        />
                        {submitError && (
                            <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">

                                <Text variant="text-xs/leading-normal/normal" className="text-charcoal-600">
                                    Försök gärna igen om en stund. Om felet kvarstår, kontakta supporten.
                                </Text>
                            </div>
                        )}
                    </>
                }

                {/* Step 5 */}
                {currentStep === 5 &&
                    <>
                        <div className="flex min-h-[300px] w-full flex-col items-center justify-center text-center">
                            <div className="mb-8 flex h-28 w-28 items-center justify-center rounded-2xl bg-white shadow-sm">

                                <Icon name="CheckSimple" size="xxlarge" className="fill-green-spruce-700" />
                            </div>
                            <Text variant="text-xl/leading-normal/medium" className="mb-3">
                                Klar!
                            </Text>
                            <Text variant="text-sm/leading-normal/normal" className="text-[#737373]">
                                Tack för ditt förtroende! Vi kommer att gå igenom informationen och jämföra den med alla de virkesaffärer vi gjort tidigare. Vi återkommer till dig när vår analys är klar.
                            </Text>
                        </div>
                    </>
                }
            </Modal.Content>
            <Modal.Footer>
                <Button variant="primary" className="w-full" disabled={!isCurrentStepValid() || isSubmitting} onClick={
                    currentStep === totalSteps
                        ? () => setOpenVirkespriskollen(false)
                        : currentStep === 4
                            ? () => handleSubmit()
                            : handleNext
                }>
                    {getButtonText()}
                </Button>
            </Modal.Footer>
        </Modal >
        </div>
    )
}

